import { Dispatch, SetStateAction, useMemo } from 'react';
import * as R from 'ramda';

import InputDateRange from '@atom/components/common/inputDateRange/InputDateRange';
import UsersFilter from '@atom/components/common/usersFilter/UsersFilter';
import { Select, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { AssetDetail } from '@atom/types/asset';
import {
  AssetRequestsFilters,
  DescriptionOption,
} from '@atom/types/assetRequest';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import {
  convertRangeDateToMillis,
  DateRange,
  DateRangeTerminator,
} from '@atom/utilities/workOrdersDateFilterUtilities';

import {
  DESCRIPTION_LABELS,
  DESCRIPTION_OPTIONS,
  STATUS_LABELS,
} from '../assetRequestConstants';
import { getSDDOTStatusOptions } from '../customTenantUtilities';

import classes from './sddotFilters.module.css';

const { MenuItem } = Select;

const styles = {
  input: {
    marginTop: '1rem',
  },
  label: {
    color: colors.neutral.dim,
  },
};

const inputLabelSx = {
  color: `${colors.neutral.dim} !important`,
};

interface Props {
  filtersCart: AssetRequestsFilters;
  setFiltersCart: Dispatch<SetStateAction<AssetRequestsFilters>>;
  asset?: AssetDetail;
  createdPresetSelected?: DateRange;
  setCreatedPresetSelect?: Dispatch<SetStateAction<DateRange>>;
  updatedPresetSelected?: DateRange;
  setUpdatedPresetSelect?: Dispatch<SetStateAction<DateRange>>;
}

const SDDOTInventoryFilters = ({
  filtersCart,
  setFiltersCart,
  asset,
  createdPresetSelected,
  setCreatedPresetSelect,
  updatedPresetSelected,
  setUpdatedPresetSelect,
}: Props) => {
  const updateFilter = (value: any, property: keyof AssetRequestsFilters) => {
    setFiltersCart(prev => ({ ...prev, [property]: value }));
  };

  const getDateValue = (property: keyof AssetRequestsFilters) => {
    if (!isNilOrEmpty(filtersCart[property])) {
      return new Date(Number(filtersCart[property]));
    }

    return null;
  };

  const handleDateChange =
    (
      property: keyof AssetRequestsFilters,
      terminator: DateRangeTerminator,
      preset?: DateRange,
    ) =>
    (val: Date) => {
      updateFilter(convertRangeDateToMillis(val, terminator, preset), property);
    };

  const handleDescriptionUpdate = event => {
    const option = event.target.value;

    const updatedFilters = {
      [DescriptionOption.ALL]: {
        assetId: asset?.id,
        fromAssetId: asset?.id,
      },
      [DescriptionOption.OUTGOING]: {
        assetId: asset?.id,
        fromAssetId: null,
      },
      [DescriptionOption.INCOMING]: {
        assetId: null,
        fromAssetId: asset?.id,
      },
    };

    setFiltersCart(prev => ({ ...prev, ...updatedFilters[option] }));
  };

  const descriptionValue = useMemo(() => {
    switch (true) {
      case !isNilOrEmpty(filtersCart.assetId) &&
        !isNilOrEmpty(filtersCart.fromAssetId): {
        return DescriptionOption.ALL;
      }
      case !isNilOrEmpty(filtersCart.assetId) &&
        isNilOrEmpty(filtersCart.fromAssetId): {
        return DescriptionOption.OUTGOING;
      }
      case isNilOrEmpty(filtersCart.assetId) &&
        !isNilOrEmpty(filtersCart.fromAssetId): {
        return DescriptionOption.INCOMING;
      }
      default:
        return '';
    }
  }, [filtersCart.assetId, filtersCart.fromAssetId]);

  const statusOptions = getSDDOTStatusOptions(filtersCart.type);
  const showDescriptionFilter = !isNilOrEmpty(asset);

  return (
    <>
      <div className={classes.inputContainer}>
        <TextField
          style={styles.input}
          slotProps={{ inputLabel: { style: styles.label } }}
          type="number"
          value={filtersCart.name}
          placeholder="Enter number only"
          label="Request ID"
          onChange={event => updateFilter(String(event.target.value), 'name')}
        />
      </div>
      <div className={classes.inputContainer}>
        <Select
          key="status"
          multiple
          displayEmpty
          label="Status"
          value={filtersCart?.statuses || []}
          onChange={event => updateFilter(event.target.value, 'statuses')}
          renderValue={R.length(filtersCart?.statuses) > 0 ? null : () => 'All'}
          inputLabelSx={inputLabelSx}
        >
          {statusOptions.map(status => (
            <MenuItem key={status} value={status}>
              {STATUS_LABELS[status]}
            </MenuItem>
          ))}
        </Select>
      </div>
      {showDescriptionFilter && (
        <div className={classes.inputContainer}>
          <Select
            key="description"
            label="Description"
            InputLabelProps={{ style: styles.label }}
            fullWidth
            onChange={event => handleDescriptionUpdate(event)}
            value={descriptionValue}
          >
            {DESCRIPTION_OPTIONS.map((option: string) => (
              <MenuItem key={option} value={option}>
                {DESCRIPTION_LABELS[option]}
              </MenuItem>
            ))}
          </Select>
        </div>
      )}
      <div className={classes.inputContainer}>
        <UsersFilter
          value={filtersCart?.assignedTo}
          updateValue={newValue => updateFilter(newValue, 'assignedTo')}
          placeholder="Search users"
          label="Assigned To"
          showTitle
        />
      </div>
      <div className={classes.inputContainer}>
        <UsersFilter
          value={filtersCart?.createdBy}
          updateValue={newValue => updateFilter(newValue, 'createdBy')}
          placeholder="Search users"
          label="Created By"
          showTitle
        />
      </div>
      <div className={classes.inputContainer}>
        <InputDateRange
          label="Created Date"
          presetSelected={createdPresetSelected}
          startDateValue={getDateValue('createdDateStart')}
          handleStartChange={handleDateChange(
            'createdDateStart',
            DateRangeTerminator.START,
            createdPresetSelected,
          )}
          endDateValue={getDateValue('createdDateEnd')}
          handleEndChange={handleDateChange(
            'createdDateEnd',
            DateRangeTerminator.END,
            createdPresetSelected,
          )}
          handleSelectDatePreset={selected =>
            setCreatedPresetSelect(selected?.value)
          }
        />
      </div>
      <div className={classes.inputContainer}>
        <InputDateRange
          label="Resolution Date"
          presetSelected={updatedPresetSelected}
          startDateValue={getDateValue('updatedDateStart')}
          handleStartChange={handleDateChange(
            'updatedDateStart',
            DateRangeTerminator.START,
            updatedPresetSelected,
          )}
          endDateValue={getDateValue('updatedDateEnd')}
          handleEndChange={handleDateChange(
            'updatedDateEnd',
            DateRangeTerminator.END,
            updatedPresetSelected,
          )}
          handleSelectDatePreset={selected =>
            setUpdatedPresetSelect(selected?.value)
          }
        />
      </div>
    </>
  );
};

export default SDDOTInventoryFilters;
